import { handleAddComponent } from 'projectSections/sections/design/util/handleAddComponent'
import { executeCustomHandler, hasCustomHandler } from './propertyHandlers'
import type {
  ComponentAddOperation,
  ComponentRemoveOperation,
  ComponentUpdateOperation,
  IncentiveAddOperation,
  IncentiveRemoveOperation,
  IncentiveUpdateOperation,
  SystemEditOperation,
  SystemPropertyUpdate,
} from './systemEditSchema'
import { validateSystemEditOperation } from './systemEditSchema'

/**
 * Applies system edit operations to the currently selected system.
 * This is a generic utility that can add/remove/update components and system properties.
 */
export async function applySystemEdit(operations: SystemEditOperation[]): Promise<void> {
  const system = window.editor?.selectedSystem
  if (!system) {
    throw new Error('No system selected. Please select a system in the design.')
  }

  if (!operations || operations.length === 0) {
    throw new Error('No operations provided')
  }

  // Validate all operations first
  for (const operation of operations) {
    const validation = validateSystemEditOperation(operation)
    if (!validation.valid) {
      throw new Error(`Invalid operation: ${validation.error}`)
    }
  }

  // Apply operations in order
  for (const operation of operations) {
    try {
      switch (operation.type) {
        case 'add':
          await applyAddComponent(system, operation)
          break

        case 'remove':
          await applyRemoveComponent(system, operation)
          break

        case 'update':
          await applyUpdateComponent(system, operation)
          break

        case 'updateProperty':
          await applyUpdateSystemProperty(system, operation)
          break

        case 'addIncentive':
          await applyAddIncentive(system, operation)
          break

        case 'removeIncentive':
          await applyRemoveIncentive(system, operation)
          break

        case 'updateIncentive':
          await applyUpdateIncentive(system, operation)
          break

        default:
          throw new Error(`Unknown operation type: ${(operation as any).type}`)
      }
    } catch (error: any) {
      console.error(`Error applying operation ${operation.type}:`, error)
      throw new Error(`Failed to apply ${operation.type} operation: ${error.message}`)
    }
  }

  console.log('System edit applied successfully')
}

async function applyAddComponent(system: any, operation: ComponentAddOperation) {
  const params: any = {}

  switch (operation.componentType) {
    case 'inverter':
      params.inverterId = operation.componentId
      break
    case 'battery':
      params.batteryId = operation.componentId
      if (operation.quantity !== undefined) {
        params.quantity = operation.quantity
      }
      break
    case 'other':
      params.otherId = operation.componentId
      if (operation.quantity !== undefined) {
        params.quantity = operation.quantity
      }
      if (operation.slotKey) {
        params.slotKey = operation.slotKey
      }
      params.autoAdded = false // Manual additions via chat
      break
  }

  const addedObject = handleAddComponent(operation.componentType, system.uuid, params)
  if (!addedObject) {
    throw new Error(`Failed to add ${operation.componentType} component`)
  }
}

async function applyRemoveComponent(system: any, operation: ComponentRemoveOperation) {
  let component: any = null

  switch (operation.componentType) {
    case 'inverter':
      component = system.inverters().find((inv: any) => inv.uuid === operation.componentUuid)
      break
    case 'battery':
      component = system.batteries().find((bat: any) => bat.uuid === operation.componentUuid)
      break
    case 'other':
      component = system.others().find((oth: any) => oth.uuid === operation.componentUuid)
      break
  }

  if (!component) {
    throw new Error(`Component ${operation.componentType} with UUID ${operation.componentUuid} not found in system`)
  }

  // For inverters, we need to remove MPPTs first
  if (operation.componentType === 'inverter') {
    const mppts = component.mppts?.() || []
    const commandUUID = window.Utils.generateCommandUUIDOrUseGlobal()
    mppts.forEach((mppt: any) => {
      window.editor.execute(new window.RemoveObjectCommand(mppt, true, false, commandUUID))
    })
  }

  window.editor.execute(new window.RemoveObjectCommand(component, true, false))
}

async function applyUpdateComponent(system: any, operation: ComponentUpdateOperation) {
  let components: any[] = []
  let component: any = null

  // Get all components of the specified type
  switch (operation.componentType) {
    case 'inverter':
      components = system.inverters()
      break
    case 'battery':
      components = system.batteries()
      break
    case 'other':
      components = system.others()
      break
  }

  // Find component by UUID (preferred), code, or ID
  if (operation.componentUuid) {
    component = components.find((comp: any) => comp.uuid === operation.componentUuid)
  } else if (operation.componentCode) {
    component = components.find((comp: any) => comp.code === operation.componentCode)
  } else if (operation.componentId) {
    const idField =
      operation.componentType === 'inverter'
        ? 'inverter_id'
        : operation.componentType === 'battery'
        ? 'battery_id'
        : 'other_id'
    component = components.find((comp: any) => comp[idField] === operation.componentId)
  }

  if (!component) {
    const identifier = operation.componentUuid
      ? `UUID ${operation.componentUuid}`
      : operation.componentCode
      ? `code ${operation.componentCode}`
      : `ID ${operation.componentId}`
    const availableComponents = components.map((comp: any) => ({
      uuid: comp.uuid,
      code: comp.code,
      quantity: comp.quantity,
      manufacturer: comp.manufacturer_name,
    }))
    throw new Error(
      `Component ${
        operation.componentType
      } with ${identifier} not found in system. Available components: ${JSON.stringify(availableComponents)}`
    )
  }

  // If multiple components match and user didn't specify UUID, warn or use first match
  if (!operation.componentUuid && components.length > 1) {
    const matchingComponents = operation.componentCode
      ? components.filter((comp: any) => comp.code === operation.componentCode)
      : operation.componentId
      ? components.filter((comp: any) => {
          const idField =
            operation.componentType === 'inverter'
              ? 'inverter_id'
              : operation.componentType === 'battery'
              ? 'battery_id'
              : 'other_id'
          return comp[idField] === operation.componentId
        })
      : []
    if (matchingComponents.length > 1) {
      console.warn(
        `Multiple ${operation.componentType} components found matching the criteria. Updating the first one: ${component.uuid}`
      )
    }
  }

  // Update quantity if provided (only for batteries and others)
  if (operation.quantity !== undefined) {
    if (operation.componentType === 'battery' || operation.componentType === 'other') {
      window.editor.execute(new window.SetValueCommand(component, 'quantity', operation.quantity))
    } else {
      throw new Error(`Cannot update quantity for ${operation.componentType} components`)
    }
  }
}

async function applyUpdateSystemProperty(system: any, operation: SystemPropertyUpdate) {
  if (hasCustomHandler(operation.property)) {
    executeCustomHandler(system, operation)
    return
  }

  window.editor.execute(new window.SetValueCommand(system, operation.property, operation.value))
}

async function applyAddIncentive(system: any, operation: IncentiveAddOperation) {
  const propertyName = `incentive_to_${operation.incentiveType}`
  const currentIncentives = system[propertyName] || []

  const newIncentive: any = {
    title: operation.title,
    value: operation.value,
    new: true, // Temporary flag for UI
  }

  if (operation.description !== undefined) {
    newIncentive.description = operation.description
  }

  if (operation.incentiveType === 'customer' && operation.use_for_loan_pay_down !== undefined) {
    newIncentive.use_for_loan_pay_down = operation.use_for_loan_pay_down
  }

  const updatedIncentives = [...currentIncentives, newIncentive]
  window.editor.execute(new window.SetValueCommand(system, propertyName, updatedIncentives))

  // Trigger system recalculation
  system.refreshUserData()
  window.editor.signals.objectChanged.dispatch(system)
}

async function applyRemoveIncentive(system: any, operation: IncentiveRemoveOperation) {
  const propertyName = `incentive_to_${operation.incentiveType}`
  const currentIncentives = system[propertyName] || []

  if (operation.incentiveIndex < 0 || operation.incentiveIndex >= currentIncentives.length) {
    throw new Error(
      `Invalid incentive index ${operation.incentiveIndex} for ${operation.incentiveType} incentives (array length: ${currentIncentives.length})`
    )
  }

  const updatedIncentives = [...currentIncentives]
  updatedIncentives.splice(operation.incentiveIndex, 1)

  window.editor.execute(new window.SetValueCommand(system, propertyName, updatedIncentives))

  // Trigger system recalculation
  system.refreshUserData()
  window.editor.signals.objectChanged.dispatch(system)
}

async function applyUpdateIncentive(system: any, operation: IncentiveUpdateOperation) {
  const propertyName = `incentive_to_${operation.incentiveType}`
  const currentIncentives = system[propertyName] || []

  if (operation.incentiveIndex < 0 || operation.incentiveIndex >= currentIncentives.length) {
    throw new Error(
      `Invalid incentive index ${operation.incentiveIndex} for ${operation.incentiveType} incentives (array length: ${currentIncentives.length})`
    )
  }

  const updatedIncentives = [...currentIncentives]
  const incentiveToUpdate = { ...updatedIncentives[operation.incentiveIndex] }

  if (operation.title !== undefined) {
    incentiveToUpdate.title = operation.title
  }
  if (operation.value !== undefined) {
    incentiveToUpdate.value = operation.value
  }
  if (operation.description !== undefined) {
    incentiveToUpdate.description = operation.description
  }
  if (operation.incentiveType === 'customer' && operation.use_for_loan_pay_down !== undefined) {
    incentiveToUpdate.use_for_loan_pay_down = operation.use_for_loan_pay_down
  }

  updatedIncentives[operation.incentiveIndex] = incentiveToUpdate

  window.editor.execute(new window.SetValueCommand(system, propertyName, updatedIncentives))

  // Trigger system recalculation
  system.refreshUserData()
  window.editor.signals.objectChanged.dispatch(system)
}
